import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@byscott-io/corebyscott";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { api } from "../lib/api";

export default function Values() {
  const { currentContainer } = useAuth();
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [selectedEntity, setSelectedEntity] = useState(null);

  const { data: metrics } = useQuery({
    queryKey: ["metric-definitions", currentContainer?.id],
    queryFn: api.getMetricDefinitions,
    enabled: !!currentContainer,
  });

  const { data: values, isLoading } = useQuery({
    queryKey: ["values", currentContainer?.id, selectedMetric],
    queryFn: () => api.getValues({ metric: selectedMetric }),
    enabled: !!currentContainer && !!selectedMetric,
  });

  const { data: history } = useQuery({
    queryKey: ["value-history", selectedMetric, selectedEntity],
    queryFn: () => {
      const [type, id] = selectedEntity.split(":");
      return api.getValueHistory(selectedMetric, type, id);
    },
    enabled: !!selectedMetric && !!selectedEntity,
  });

  if (!currentContainer) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Select an app to view metric values</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Metric Values</h1>
        <p className="text-gray-500">Time-series metrics and data warehouse</p>
      </div>

      {/* Metric Selection */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Select Metric</label>
            <select
              value={selectedMetric || ""}
              onChange={(e) => {
                setSelectedMetric(e.target.value);
                setSelectedEntity(null);
              }}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            >
              <option value="">Choose a metric...</option>
              {metrics?.map((m) => (
                <option key={m.name} value={m.name}>{m.name}</option>
              ))}
            </select>
          </div>
          {selectedMetric && values?.entities && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Select Entity</label>
              <select
                value={selectedEntity || ""}
                onChange={(e) => setSelectedEntity(e.target.value)}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              >
                <option value="">Choose an entity...</option>
                {values.entities.map((e) => (
                  <option key={`${e.entity_type}:${e.entity_id}`} value={`${e.entity_type}:${e.entity_id}`}>
                    {e.entity_type} #{e.entity_id}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* History Chart */}
      {selectedEntity && history?.data && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {selectedMetric} History
          </h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={history.data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="start_time"
                  tickFormatter={(v) => new Date(v).toLocaleDateString()}
                />
                <YAxis />
                <Tooltip
                  labelFormatter={(v) => new Date(v).toLocaleString()}
                  formatter={(v) => [v, selectedMetric]}
                />
                <Line
                  type="stepAfter"
                  dataKey="value"
                  stroke="#6366f1"
                  strokeWidth={2}
                  dot={true}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Current Values Table */}
      {selectedMetric && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">Current Values: {selectedMetric}</h2>
          </div>
          {isLoading ? (
            <div className="p-8 text-center text-gray-500">Loading values...</div>
          ) : values?.current?.length > 0 ? (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entity</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Value</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Since</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {values.current.map((v) => (
                  <tr
                    key={`${v.entity_type}:${v.entity_id}`}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedEntity(`${v.entity_type}:${v.entity_id}`)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{v.entity_type}</span>
                      <span className="text-sm text-gray-500 ml-2">#{v.entity_id}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                      {Number(v.value).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(v.start_time).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="p-8 text-center text-gray-500">No values recorded for this metric</div>
          )}
        </div>
      )}

      {/* Metrics Overview */}
      {!selectedMetric && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Available Metrics</h2>
          {metrics?.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {metrics.map((m) => (
                <button
                  key={m.name}
                  onClick={() => setSelectedMetric(m.name)}
                  className="p-4 border rounded-lg hover:border-indigo-500 hover:bg-indigo-50 text-left transition-colors"
                >
                  <p className="font-medium text-gray-900">{m.name}</p>
                  <p className="text-sm text-gray-500">{m.entity_count || 0} entities</p>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No metrics defined yet</p>
          )}
        </div>
      )}
    </div>
  );
}
